import { Method } from 'axios';
import { ECurrency, ELanguage, ESalutation } from '@ringana/configs';
import {
  IApiCoupon,
  IApiCustomerDataSheet,
  IApiDataSheetOrder,
  IApiDatasheetOrdersFilter,
  IApiGiveawayRegistrationFormData,
  IApiNewsletterFormData,
  IApiPartnerInfo,
  IApiSearchResult,
  IBlogPostRequestData,
} from '../common/interfaces/api.interface';
import { IApiBarometerData } from '../common/interfaces/barometer.interface';
import { ICurrency } from '../common/interfaces/index.interface';
import { IDashboardData } from '../common/interfaces/dashboard.interface';
import { IProduct } from '../common/interfaces/products.interface';
import { IBouncerConfig, ICountry, ISalutation } from '../common/interfaces/config.interface';
import { IUpdateAddress } from '../common/interfaces/my-profile.interface';
import { IAddress, IAddressData } from '../common/interfaces/shared.interface';
import { ICreateShoppingCart, IShoppingCart } from '../common/interfaces/cart.interface';
import {
  IDocuSignRecipientViewData,
  IFrenchPartnerApplicationDocumentOptions,
  IGenericPartnerApplicationDocumentOptions,
  IItalianPartnerApplicationDocumentOptions,
  IPartnerApplication,
  IPartnerApplicationResponse,
  IPartnerApplicationSendPADto,
} from '../common/interfaces/partner-application.interface';
import {
  IMasterSummitDashboardData,
  IPartnerSearchResult,
  ISpecialSummitDashboardData,
  IUplineData,
} from '../common/interfaces/partners.interface';
import { ICheckoutTaxNumberData } from '../common/interfaces/checkout.interface';
import {
  IIncentive,
  IIncentiveRanking,
  IIncentivesData,
  ISearchResults,
  IVotePartner,
} from '../common/interfaces/single-incentive.interface';
import { IImage } from '../common/interfaces/image.interface';
import { IBlogPostsData } from '../common/interfaces/blog.interface';
import { ICustomer, ICustomerSession } from '../common/interfaces/customers.interface';
import { IChannelizeBroadcast } from '../common/interfaces/channelize.interface';
import { IApiEvent } from '../common/interfaces/events.interface';
import {
  IDeleteCartItemRequestData,
  IGetCalendarRequestData,
  IPatchCartItemRequestData,
  IPostCartItemRequestData,
  IUpdateProductVariantRequestData,
} from '../common/interfaces/request.interface';
import { ICalendarData } from '../common/interfaces/calendar.interface';
import { TSearchRole } from '../common/types/index.types';
import ApiRoutes from './ApiRoutes';
import config from '../../config';
import { TApiResponse } from '../common/types/api.types';
import { ICoordinates, IMapParams } from '../common/interfaces/location.interface';
import {
  INewVendorVolumeData,
  ITopNewPartnerVolumePartner,
} from '@/common/interfaces/team.interface';

const ZAPI_BASEURL: string = config.api.baseurl;

export default class ZApiRoutes<TResponse = undefined, TRequestData = undefined, TRequestParams = undefined> extends ApiRoutes<TApiResponse<TResponse>, TRequestData, TRequestParams> {
  // Channelize
  public static getBroadcasts = new ZApiRoutes<IChannelizeBroadcast[]>(`/channelize/broadcasts`, `get`);

  // Customers
  public static getAddressById = new ZApiRoutes<IAddress[], undefined, { addressId: number }>(`/customers/addresses/:addressId`, `get`, true);
  public static getAddresses = new ZApiRoutes<IAddress[], { customerNumber?: number }>(`/customers/addresses`, `get`, true);
  public static getCalendarData = new ZApiRoutes<ICalendarData, IGetCalendarRequestData>(`/customers/calendar`, `get`, true);
  public static getCustomer = new ZApiRoutes<ICustomer, { customerNumber?: number }>(`/customers`, `get`, true);
  public static getCustomerDatasheet = new ZApiRoutes<IApiCustomerDataSheet, { customerNumber: number }>(`/customers/datasheet`, `get`, true);
  public static getCustomerSession = new ZApiRoutes<ICustomerSession>(`/customers/session`, `get`, true);
  public static getDatasheetOrders = new ZApiRoutes<IApiDataSheetOrder[], IApiDatasheetOrdersFilter & { limit: number; offset: number }>(`/customers/datasheet-orders`, `get`, true);
  public static getLoginCheck = new ZApiRoutes(`/customers/login/check`, `get`, true);
  public static patchNewAddress = new ZApiRoutes<IAddress, IAddress | IUpdateAddress>(`/customers/address`, `patch`, true);
  public static postNewsletterConfirm = new ZApiRoutes<undefined, { action: string; checksum: string; email: string }>(`/customers/newsletter`, `post`, true);
  public static transferCustomer = new ZApiRoutes<undefined, { customerNumber: number; partner: number }>(`/customers/transfer`, `post`, true);
  public static unsubscribeNewsletter = new ZApiRoutes<undefined, { email: string; salutationId: ESalutation }>(`/customers/newsletter/unsubscribe`, `post`, true);
  public static updateNewsletter = new ZApiRoutes<undefined, IApiNewsletterFormData>(`/customers/newsletter`, `patch`, true);
  public static verifyTaxNumber = new ZApiRoutes<boolean, ICheckoutTaxNumberData>(`/customers/tax-numbers`, `post`, true);

  // Carts
  public static addSupplement = new ZApiRoutes<IShoppingCart, { cartId: number; supplementId: number; isPartnerApplication: boolean }>(`/carts/supplement`, `post`, true);
  public static deleteCart = new ZApiRoutes<boolean, { cartId: number }>(`/carts`, `delete`, true);
  public static deleteCoupon = new ZApiRoutes<IShoppingCart, { cartContentId: number }>(`/carts/coupon`, `get`, true);
  public static deleteItemFromCart = new ZApiRoutes<IShoppingCart, IDeleteCartItemRequestData>(`/carts/item`, `delete`, true);
  public static getCart = new ZApiRoutes<IShoppingCart, undefined, { id: number | string }>(`/carts/:id`, `get`, true);
  public static getCartCount = new ZApiRoutes<number>(`/carts/count`, `get`, true);
  public static getCoupons = new ZApiRoutes<IApiCoupon[], { customerNumber: number; genericAutoredeem?: boolean }>(`/carts/coupons`, `get`, true);
  public static patchCartItem = new ZApiRoutes<IShoppingCart, IPatchCartItemRequestData>(`/carts/item`, `patch`, true);
  public static postCartCreate = new ZApiRoutes<ICreateShoppingCart & IShoppingCart, { customerNumber?: number; type?: number }>(`/carts/create`, `post`, true);
  public static postItemToCart = new ZApiRoutes<IShoppingCart, IPostCartItemRequestData>(`/carts/item`, `post`, true);
  public static redeemCoupon = new ZApiRoutes<IShoppingCart, { cartId: number; code: string; isPartnerApplication: boolean }>(`/carts/coupon`, `post`, true);
  public static removeCoupon = new ZApiRoutes<IShoppingCart, { cartContentId: number; cartId: number; code: string; isPartnerApplication: boolean }>(`/carts/coupon`, `delete`, true);
  public static updateProductVariant = new ZApiRoutes<IShoppingCart, IUpdateProductVariantRequestData>(`/carts/item`, `put`, true);

  // Partners
  public static getFairScore = new ZApiRoutes<number, { customerNumber: number }>(`/partners/fair-score`, `get`, true);
  public static getIncentiveData = new ZApiRoutes<IIncentivesData, undefined, { id: number }>(`/partners/incentives/:id/data`, `get`, true);
  public static getIncentiveRanking = new ZApiRoutes<IIncentiveRanking, { limit: number }, { id: number }>(`/partners/incentives/:id/ranking`, `get`, true);
  public static getIncentives = new ZApiRoutes<IIncentive[]>(`/partners/incentives/`, `get`, true);
  public static getItalianPartnerPicture = new ZApiRoutes<IImage>(`/partners/italian/picture`, `get`, true);
  public static getMasterSummitDashboardData = new ZApiRoutes<IMasterSummitDashboardData, undefined, { id: number }>(`/partners/incentives/:id/master-summit-dashboard-data`, `get`, true);
  public static getPartnerApplication = new ZApiRoutes<IPartnerApplication, undefined, { addressId: string }>(`/partners/application/:addressId`, `get`, true);
  public static getPartnerInfo = new ZApiRoutes<IApiPartnerInfo, undefined, { name: string }>(`/partnerinfo/:name`, `get`, true);
  public static getSearchResults = new ZApiRoutes<ISearchResults[], undefined, { id: number; search: string }>(`/partners/incentives/search/:idincentive/:search`, `post`, true);
  public static getSpecialSummitDashboardData = new ZApiRoutes<ISpecialSummitDashboardData, undefined, { id: number }>(`/partners/incentives/:id/special-summit-dashboard-data`, `get`, true);
  public static getUpline = new ZApiRoutes<IUplineData[], { customerNumber: number } | undefined>(`/partners/upline`, `get`, true);
  public static postItalianPartnerPicture = new ZApiRoutes<IImage, IImage>(`/partners/italian/picture`, `post`, true);
  public static postPartnerApplication = new ZApiRoutes<IPartnerApplicationResponse, IPartnerApplicationSendPADto>(`/partners/application`, `post`, true);
  public static postPartnerApplicationPreCheck = new ZApiRoutes<IPartnerApplicationResponse, IPartnerApplicationSendPADto>(`/partners/application/check`, `post`, true);
  public static postPartnerImage = new ZApiRoutes<string, FormData>(`/partners/image`, `post`, true);
  public static postPartnerSearch = new ZApiRoutes<IPartnerSearchResult[], { searchString: string }>(`/partners/search`, `post`, true);
  public static postVendorRecommendation = new ZApiRoutes<undefined, { customerNumber: number }>(`/partners/vendor-recommendation`, `post`, true);
  public static updateItalianPartnerPicture = new ZApiRoutes<IImage, IImage>(`/partners/italian/picture`, `patch`, true);
  public static votePartner = new ZApiRoutes<IVotePartner, undefined, { cn: number; id: number }>(`/partners/incentives/vote/:idincentive/:cn`, `post`, true);

  // Products
  public static getPartnerApplicationProducts = new ZApiRoutes<IProduct[]>(`/products/partner-applications`, `get`, true);
  public static getProductByMatchcode = new ZApiRoutes<IProduct, undefined, { matchcode: string }>(`/products/:matchcode`, `get`, true);
  public static getReducedProducts = new ZApiRoutes<IProduct[], { currency: ECurrency; language: ELanguage }>(`/products/reduced`, `get`, true);

  // Configs
  public static getAllCountries = new ZApiRoutes<ICountry[]>(`/configs/countries/all`, `get`, true);
  public static getCountries = new ZApiRoutes<ICountry[]>(`/configs/countries`, `get`, true);
  public static getCurrencies = new ZApiRoutes<ICurrency[]>(`/configs/currencies`, `get`, true);
  public static getSalutations = new ZApiRoutes<ISalutation[]>(`/configs/salutations`, `get`, true);
  public static getBouncerConfig = new ZApiRoutes<IBouncerConfig>(`/configs/bouncer`, `get`);

  // Statistics
  public static getDashboardData = new ZApiRoutes<IDashboardData>(`/statistics/partner-dashboard`, `get`, true);

  // Search
  public static getBlogPosts = new ZApiRoutes<IBlogPostsData[], IBlogPostRequestData>(`/search/blog-posts-wp`, `get`, true);
  public static getSearchData = new ZApiRoutes<IApiSearchResult[], { lang: ELanguage; limit: number; showHiddenCategories: boolean; term: string }, { role: TSearchRole }>(`/search/post-types/:role`, `get`, true);

  // Teams
  public static postBarometerData = new ZApiRoutes<IApiBarometerData>(`/teams/target-levels/data`, `post`, true);
  public static sendVoucherAssignmentMail = new ZApiRoutes<undefined, { voucherCode: string }>(`/teams/voucher-assignment-mail`, `post`, true);
  public static getNewVendorVolumes = new ZApiRoutes<INewVendorVolumeData, { invoicingId?: number }>(`/teams/new-vendor-volumes`, `get`, true);
  public static getTopNewPartnerVolumePartners = new ZApiRoutes<ITopNewPartnerVolumePartner[], { amount?: number; invoicingId?: number }>(`/teams/top-new-partner-volume-partners`, `get`, true);

  // Document Signing
  public static createDocuSignRecipientView = new ZApiRoutes<IDocuSignRecipientViewData, IItalianPartnerApplicationDocumentOptions | IFrenchPartnerApplicationDocumentOptions | IGenericPartnerApplicationDocumentOptions>(`/document-signing/recipient-view`, `post`);

  // Images
  public static deleteImage = new ZApiRoutes<undefined, undefined, { key: string }>(`/images/:key`, `delete`, true);
  public static postImage = new ZApiRoutes<IImage, FormData>(`/images`, `post`, true);

  // Order Switch Information Endpoint
  public static getSwitchOrderRoutes = new ZApiRoutes<Record<string, string[]>>(`/configs/order/routes`, `get`);

  // Ungerboeck
  public static getEvents = new ZApiRoutes<IApiEvent[]>(`/events`, `get`, true);

  // Location
  public static getCoordinates = new ZApiRoutes<ICoordinates, IMapParams>(`/location/coordinates`, `get`);

  // Giveaway
  public static registerForGiveawayDoor = new ZApiRoutes<undefined, IApiGiveawayRegistrationFormData>(`/customers/giveaway`, `post`, true);

  // Checkout
  public static checkoutAddresses = new ZApiRoutes<IAddressData, { cartId: string | number; partnerShop?: string }>(`/checkout/addresses`, `post`, true);

  private constructor(url: string, method: Method, withCredentials = false) {
    super(url, method, withCredentials, ZAPI_BASEURL);
  }
}
